import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, DrawingManager, Marker, OverlayView, Circle, HeatmapLayer, InfoWindow, DirectionsRenderer } from '@react-google-maps/api';

// Track Google Maps API usage for FinOps
const TRACKING_API = 'https://property-lookup-api.anassk.workers.dev';
function trackMapsUsage(apiType, count = 1) {
  const userId = localStorage.getItem('leadgen_client_id') || 'anonymous';
  const email = localStorage.getItem('leadgen_user_email') || null;
  fetch(`${TRACKING_API}/track/maps`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, email, apiType, count })
  }).catch(() => {}); // Fire and forget
}

/**
 * Hotspot zones for heatmap overlay - verified storm + wealthy areas (Indiana only, hail >= 0.75")
 *
 * STORM DATA SOURCES (NOAA Storm Prediction Center):
 * - May 20, 2025: https://www.spc.noaa.gov/climo/reports/250520_rpts.html
 *   - Southport (Marion Co): 1.00" hail at 39.65°N, 86.04°W
 * - Apr 2, 2025: https://www.spc.noaa.gov/climo/reports/250402_rpts.html
 *   - Fort Wayne area (Allen Co): 1.00-1.75" hail
 *   - Murray (Wells Co): 1.75" hail
 * - Mar 19, 2025: https://www.spc.noaa.gov/climo/reports/250319_rpts.html
 *   - Lebanon (Boone Co): 2.00" hail
 *   - Williams (Martin Co): 2.00" hail
 *   - Scipio (Jennings Co): 1.00" hail
 * - Mar 30, 2025: https://www.spc.noaa.gov/climo/reports/250330_rpts.html
 *   - Valparaiso (Porter Co): 81 mph wind
 *   - Terre Haute (Vigo Co): 0.75-1.00" hail
 */
const HOTSPOT_ZONES = [
  // Verified hail >= 0.75" (from NOAA/SPC reports above)
  { zip: '46227', name: 'Southport', lat: 39.65, lng: -86.04, stormPriority: true, stormNote: '1" hail May 20', score: 95 },
  { zip: '46804', name: 'Fort Wayne SW', lat: 41.03, lng: -85.22, stormPriority: true, stormNote: '1.5" hail Apr 2', score: 94 },
  { zip: '46714', name: 'Bluffton', lat: 40.74, lng: -85.17, stormPriority: true, stormNote: '1" hail Apr 2', score: 90 },
  { zip: '46052', name: 'Lebanon', lat: 40.05, lng: -86.47, stormPriority: true, stormNote: '2" hail Mar 19', score: 98 },
  { zip: '47274', name: 'Scipio', lat: 38.87, lng: -85.71, stormPriority: true, stormNote: '1" hail Mar 19', score: 88 },
  { zip: '47384', name: 'Terre Haute', lat: 39.47, lng: -87.41, stormPriority: true, stormNote: '1" hail Mar 30', score: 86 },
  { zip: '46236', name: 'Geist', lat: 39.915, lng: -85.961, stormPriority: true, stormNote: 'Dec wind', score: 82, wealthy: true },
  // Wealthy areas (income data from Census)
  { zip: '46259', name: 'Franklin Twp', lat: 39.652, lng: -85.987, wealthy: true, income: 123000, score: 88 },
  { zip: '46278', name: 'Eagle Creek', lat: 39.886, lng: -86.291, wealthy: true, income: 148000, score: 92 },
  { zip: '46220', name: 'Broad Ripple', lat: 39.869, lng: -86.139, wealthy: true, income: 94000, score: 85 },
  { zip: '46032', name: 'Carmel', lat: 39.978, lng: -86.118, wealthy: true, income: 125000, score: 90 },
  { zip: '46077', name: 'Zionsville', lat: 39.951, lng: -86.262, wealthy: true, income: 140000, score: 88 },
  { zip: '46037', name: 'Fishers', lat: 39.955, lng: -85.968, wealthy: true, income: 115000, score: 85 },
];

// ========== WEEK PLAN ROUTE DATA (Feb 23-27) ==========
const WEEK_PLAN = [
  {
    day: 'MON', label: 'Monday · Geist Lake', color: '#d62828',
    center: { lat: 39.921, lng: -85.975 },
    stops: [
      { num: '1', address: '12670 Shorevista Dr, Lawrence, IN 46236', owner: 'Melissa Kevorkian', roofAge: 31, value: 1620000, quality: 'hot' },
      { num: '2', address: '11038 Tenacious Dr, Lawrence, IN 46236', owner: 'Eric & Gail Henricks', roofAge: 42, quality: 'hot' },
      { num: '3', address: '8510 Jib Ct, Lawrence, IN 46236', owner: 'Gary & Mary Beth Myers', roofAge: 40, quality: 'hot' },
      { num: '4', address: '10358 Courageous Dr, Lawrence, IN 46236', owner: 'Hedegard Trust', roofAge: 37, value: 1070000, quality: 'warm' },
      { num: '5', address: '8262 Bowline Ct, Lawrence, IN 46236', owner: 'John & Lori Zehner', roofAge: 38, quality: 'warm' },
      { num: '6', address: '8596 Twin Pointe Cir, Lawrence, IN 46236', owner: 'Andrew & Hermiene Ording', roofAge: 38, quality: 'warm' },
      { num: '7', address: '9052 Diamond Pointe Dr, Lawrence, IN 46236', owner: 'Kevin & Jennifer Siminski', roofAge: 35, value: 1025000, quality: 'warm' },
      { num: '8', address: '8211 Lake Springs Ct, Lawrence, IN 46236', owner: 'Robert & Stephanie Gardner', roofAge: 31, quality: 'good' },
    ],
    stats: { doors: 8, inspections: '2–3', avgJob: '$8–12K', potential: '$35K', conversion: '25–37%' }
  },
  {
    day: 'TUE', label: 'Tuesday · SE Indy Storm Zone', color: '#0096c7',
    center: { lat: 39.672, lng: -86.058 },
    stops: [
      { num: '1', address: '9515 E Edgewood Ave, Indianapolis, IN 46239', owner: 'Patricia Collier', roofAge: 32, value: 667000, quality: 'hot' },
      { num: '2', address: '2410 S Post Rd, Indianapolis, IN 46239', owner: 'Robert McKinney', value: 300000, quality: 'warm' },
    ],
    stats: { doors: '~40', inspections: '3–5', avgJob: '$6–9K', potential: '$30K', conversion: '10–12%' }
  },
  {
    day: 'WED', label: 'Wednesday · Brownsburg + Carmel', color: '#40916c',
    center: { lat: 39.88, lng: -86.23 },
    stops: [
      { num: '1', address: '1 N Green St, Brownsburg, IN 46112', owner: 'EF-2 Tornado Zone', quality: 'hot' },
      { num: '2', address: 'Arbuckle Rd, Brownsburg, IN 46112', owner: 'Tornado Path', quality: 'hot' },
      { num: '3', address: 'Carmel, IN 46033', owner: 'EF-1 Zone · $500K+ homes', quality: 'warm' },
    ],
    stats: { doors: '~60', inspections: '6–8', avgJob: '$8–12K', potential: '$60K', conversion: '12–13%' }
  },
  {
    day: 'THU', label: 'Thursday · Carmel + Zionsville', color: '#b5179e',
    center: { lat: 39.965, lng: -86.19 },
    stops: [
      { num: '1', address: '116th St, Carmel, IN 46032', owner: '30+ yr roofs · $600K–$1M homes', quality: 'hot' },
      { num: '2', address: 'Main St, Zionsville, IN 46077', owner: 'Historic + estate homes', quality: 'warm' },
    ],
    stats: { doors: '~50', inspections: '4–6', avgJob: '$10–15K', potential: '$65K', conversion: '10–12%' }
  },
  {
    day: 'FRI', label: 'Friday · Follow-Ups + Fishers', color: '#e85d04',
    center: { lat: 39.954, lng: -85.972 },
    stops: [
      { num: '1', address: 'Brooks School Rd & 126th St, Fishers, IN 46037', owner: '25–35yr roofs', quality: 'warm' },
      { num: '2', address: 'Brooks School Rd & 116th St, Fishers, IN 46037', owner: 'Fishers premium', quality: 'warm' },
    ],
    stats: { doors: '~50', inspections: '4–6', avgJob: '$8–12K', potential: '$50K', conversion: '10–12%' }
  }
];

// Zone Marker Component - visible at city level
function ZoneMarker({ zone, onClick, showStorms, showWealth }) {
  // Determine if this zone should be shown
  const isStormZone = zone.stormPriority && showStorms;
  const isWealthZone = zone.wealthy && showWealth;

  if (!isStormZone && !isWealthZone) return null;

  const bgColor = isStormZone ? '#f97316' : '#22c55e';
  const icon = isStormZone ? '⚡' : '💰';

  return (
    <div
      className="zone-marker"
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(zone);
      }}
      style={{ '--zone-color': bgColor }}
    >
      <div className="zone-icon">{icon}</div>
      <div className="zone-name">{zone.name}</div>
      <div className="zone-zip">{zone.zip}</div>
      {isStormZone && zone.stormNote && <div className="zone-note">{zone.stormNote}</div>}
      {isWealthZone && zone.income && <div className="zone-note">${Math.round(zone.income/1000)}K avg</div>}
    </div>
  );
}

// Format currency for map markers
function formatMarkerValue(value) {
  if (!value) return null;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return `${value}`;
}

// Cluster Marker Component - shows count when zoomed out
function ClusterMarker({ cluster, onClick }) {
  const { count, leads, center, hotCount } = cluster;

  // Size based on count - compact design
  const size = Math.min(44, 24 + Math.log10(count) * 10);

  // Color based on hot lead percentage
  const hotPercent = hotCount / count;
  const bgColor = hotPercent > 0.5 ? '#dc2626' : hotPercent > 0.2 ? '#f97316' : '#3b82f6';

  return (
    <div
      className="cluster-marker"
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(cluster);
      }}
      style={{
        '--cluster-size': `${size}px`,
        '--cluster-color': bgColor
      }}
    >
      <div className="cluster-count">{count > 999 ? `${Math.round(count / 1000)}K` : count}</div>
      {hotCount > 0 && <div className="cluster-hot-badge">{hotCount} hot</div>}
    </div>
  );
}

// Price Marker Component
function PriceMarker({ lead, onClick, isSelected }) {
  const value = lead.assessed_value || lead.assessedValue || lead.roof_cost_low || lead.roofCostAsphalt;
  const displayValue = formatMarkerValue(value);

  if (!displayValue) return null;

  const isHot = lead.priority === 'hot' || lead.leadQuality === 'hot';
  const isEnriched = lead.enriched;
  const roofConfidence = lead.roof_age_confidence || lead.roofAgeConfidence || 'low';
  const needsRoof = (lead.roof_needs_replacement || lead.needsRoofReplacement) && (roofConfidence === 'medium' || roofConfidence === 'high');

  // Color based on status
  let bgColor = '#6b7280'; // gray default
  if (needsRoof) bgColor = '#dc2626'; // red for needs roof
  else if (isHot) bgColor = '#ef4444'; // red for hot
  else if (isEnriched) bgColor = '#22c55e'; // green for enriched
  else bgColor = '#3b82f6'; // blue default

  return (
    <div
      className={`price-marker ${isSelected ? 'selected' : ''} ${isHot ? 'hot' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(lead);
      }}
      style={{ '--marker-color': bgColor }}
    >
      <span className="price-value">${displayValue}</span>
      {needsRoof && <span className="needs-roof-dot" title="Needs roof replacement"></span>}
    </div>
  );
}

// Simple clustering algorithm - groups nearby leads based on zoom level
function clusterLeads(leads, mapBounds, zoom) {
  if (!leads || leads.length === 0) return { clusters: [], unclustered: [] };

  // Filter leads with coordinates
  const leadsWithCoords = leads.filter(l => l.lat && l.lng);
  if (leadsWithCoords.length === 0) return { clusters: [], unclustered: [] };

  // If no zoom yet, use default
  const effectiveZoom = zoom || 13;

  // Cluster radius in degrees based on zoom level
  // Lower zoom = bigger clusters, higher zoom = smaller/no clusters
  const clusterRadius = effectiveZoom < 12 ? 0.03 : effectiveZoom < 14 ? 0.015 : effectiveZoom < 16 ? 0.005 : 0;

  // If zoomed in enough, show individual markers
  if (clusterRadius === 0) {
    return { clusters: [], unclustered: leadsWithCoords };
  }

  const clusters = [];
  const used = new Set();

  for (let i = 0; i < leadsWithCoords.length; i++) {
    if (used.has(i)) continue;

    const lead = leadsWithCoords[i];
    const clusterLeads = [lead];
    used.add(i);

    // Find nearby leads to cluster
    for (let j = i + 1; j < leadsWithCoords.length; j++) {
      if (used.has(j)) continue;

      const other = leadsWithCoords[j];
      const distance = Math.sqrt(
        Math.pow(lead.lat - other.lat, 2) + Math.pow(lead.lng - other.lng, 2)
      );

      if (distance < clusterRadius) {
        clusterLeads.push(other);
        used.add(j);
      }
    }

    // Only create cluster if multiple leads
    if (clusterLeads.length >= 3) {
      // Calculate cluster center
      const centerLat = clusterLeads.reduce((sum, l) => sum + l.lat, 0) / clusterLeads.length;
      const centerLng = clusterLeads.reduce((sum, l) => sum + l.lng, 0) / clusterLeads.length;

      clusters.push({
        center: { lat: centerLat, lng: centerLng },
        leads: clusterLeads,
        count: clusterLeads.length,
        hotCount: clusterLeads.filter(l => l.leadQuality === 'hot' || l.priority === 'hot').length
      });
    } else {
      // Add as individual markers
      clusters.push({
        center: { lat: lead.lat, lng: lead.lng },
        leads: clusterLeads,
        count: 1,
        hotCount: 0,
        isSingle: true
      });
    }
  }

  // Separate clusters from singles
  const realClusters = clusters.filter(c => !c.isSingle);
  const unclustered = clusters.filter(c => c.isSingle).flatMap(c => c.leads);

  return { clusters: realClusters, unclustered };
}

// Update map options when drawing mode changes

const getMapContainerStyle = (isDrawing) => ({
  width: '100%',
  height: '100%',
  minHeight: '100%',
  cursor: isDrawing ? 'crosshair' : 'grab'
});

// Dark storm tracker map style (inspired by StormerSite)
const DARK_MAP_STYLES = [
  { featureType: 'water', stylers: [{ color: '#021019' }] },
  { featureType: 'landscape', stylers: [{ color: '#08304b' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#0c4152' }, { lightness: 5 }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#0b434f' }, { lightness: 25 }] },
  { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#0b3d51' }, { lightness: 16 }] },
  { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }, { lightness: 13 }] },
  { featureType: 'transit', stylers: [{ color: '#146474' }] },
  { featureType: 'administrative', elementType: 'geometry.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#144b53' }, { lightness: 14 }, { weight: 1.4 }] }
];

// Light/satellite styles
const LIGHT_MAP_STYLES = [
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] }
];

const getMapOptions = (isDrawing, darkMode = true) => ({
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false, // We'll use our own toggle
  fullscreenControl: false,
  draggable: !isDrawing,
  scrollwheel: !isDrawing,
  disableDoubleClickZoom: isDrawing,
  clickableIcons: false,
  mapTypeId: darkMode ? 'roadmap' : 'hybrid',
  styles: darkMode ? DARK_MAP_STYLES : LIGHT_MAP_STYLES
});

/**
 * INDIANA 2025 Storm Data from StormerSite (NOAA/SPC verified)
 * Source: https://www.stormersite.com/hail_reports/indianapolis_indiana/hailmap/2025
 *
 * Focus: Indianapolis, Carmel, Fishers (Marion & Hamilton County)
 */
const INDIANA_STORM_DATA = [
  // === HAIL REPORTS (1.00" = insurance claim threshold) ===
  { type: 'hail', lat: 39.70, lng: -86.03, date: '2025-04-02', description: '1.00 inch hail', county: 'Marion', state: 'IN', magnitude: 100 },
  { type: 'hail', lat: 39.65, lng: -86.04, date: '2025-05-20', description: '1.00 inch hail (mPING)', county: 'Marion', state: 'IN', magnitude: 100 },
  { type: 'hail', lat: 39.58, lng: -86.12, date: '2025-05-16', description: '1.00 inch hail (mPING)', county: 'Marion', state: 'IN', magnitude: 100 },

  // === TORNADO REPORT (Carmel EF-1) ===
  { type: 'tornado', lat: 39.96, lng: -86.15, date: '2025-04-02', description: 'EF-1 tornado, 105 mph, 6.6 mi path through Carmel', county: 'Hamilton', state: 'IN', magnitude: 1 },

  // === WIND REPORTS (trees/structures down) ===
  // Indianapolis/Marion County
  { type: 'wind', lat: 39.76, lng: -86.13, date: '2025-04-02', description: 'Large brick building roof torn off', county: 'Marion', state: 'IN', magnitude: 70 },
  { type: 'wind', lat: 39.77, lng: -86.14, date: '2025-04-02', description: 'Fence attached to houses blown down', county: 'Marion', state: 'IN', magnitude: 65 },
  { type: 'wind', lat: 39.83, lng: -86.17, date: '2025-06-18', description: 'Numerous large oak trees down', county: 'Marion', state: 'IN', magnitude: 65 },
  { type: 'wind', lat: 39.78, lng: -86.07, date: '2025-06-18', description: 'Large tree and power lines down, 65-70 mph', county: 'Marion', state: 'IN', magnitude: 70 },
  { type: 'wind', lat: 39.70, lng: -86.06, date: '2025-05-16', description: '65-70 mph winds', county: 'Marion', state: 'IN', magnitude: 70 },
  { type: 'wind', lat: 39.72, lng: -86.16, date: '2025-03-15', description: 'Spotter estimated 60 mph winds', county: 'Marion', state: 'IN', magnitude: 60 },
  // Carmel/Fishers (Hamilton County)
  { type: 'wind', lat: 39.97, lng: -86.13, date: '2025-04-02', description: 'Building damage, Carmel Old Town', county: 'Hamilton', state: 'IN', magnitude: 70 },
  { type: 'wind', lat: 39.89, lng: -86.04, date: '2025-06-18', description: 'Large tree uprooted on house', county: 'Marion', state: 'IN', magnitude: 65 },
  { type: 'wind', lat: 39.95, lng: -86.11, date: '2025-06-18', description: 'Tree limb on Keystone Ave', county: 'Hamilton', state: 'IN', magnitude: 60 },
  { type: 'wind', lat: 39.95, lng: -86.05, date: '2025-05-16', description: 'Multiple trees down, 0.75" hail', county: 'Hamilton', state: 'IN', magnitude: 65 },
  // Hendricks County (west side)
  { type: 'wind', lat: 39.89, lng: -86.30, date: '2025-04-02', description: 'Power poles snapped, semi flipped', county: 'Hendricks', state: 'IN', magnitude: 75 },
  { type: 'wind', lat: 39.85, lng: -86.35, date: '2025-04-02', description: 'Structure collapse reported', county: 'Hendricks', state: 'IN', magnitude: 80 },
  { type: 'wind', lat: 39.86, lng: -86.35, date: '2025-04-02', description: 'Housing development damaged', county: 'Hendricks', state: 'IN', magnitude: 75 },
];

// Generate NOAA SPC report URL from storm date
// Format: https://www.spc.noaa.gov/climo/reports/YYMMDD_rpts.html
function getNoaaReportUrl(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const yy = String(date.getFullYear()).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `https://www.spc.noaa.gov/climo/reports/${yy}${mm}${dd}_rpts.html`;
}

const drawingOptions = {
  circleOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: false,  // No resize handles (cleaner look)
    draggable: true,  // Can still move
    zIndex: 1
  },
  rectangleOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: true,
    draggable: true,
    zIndex: 1
  },
  polygonOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: false,
    draggable: true,
    zIndex: 1
  }
};

// Route Stop Marker Component - numbered circles on the route
function RouteStopMarker({ stop }) {
  const bgColor = stop.quality === 'hot' ? '#dc2626' : stop.quality === 'warm' ? '#f97316' : '#22c55e';
  return (
    <div style={{
      width: 30, height: 30,
      borderRadius: '50%',
      background: bgColor,
      border: '2.5px solid #fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: '800',
      fontSize: '13px',
      transform: 'translate(-50%, -50%)',
      cursor: 'pointer',
      userSelect: 'none',
      zIndex: 1000,
      position: 'relative'
    }}>
      {stop.num}
    </div>
  );
}

// Map Filter Toolbar Component - v2 Storm Integration
function MapFilters({ filters, onFilterChange, stats }) {
  return (
    <div className="map-filters" style={{ display: 'flex' }}>
      <button
        className={`map-filter-btn ${filters.storms ? 'active storm' : ''}`}
        onClick={() => onFilterChange('storms', !filters.storms)}
        title="Show storm zones"
      >
        ⚡ Storms {stats.storms > 0 && <span className="filter-count">{stats.storms}</span>}
      </button>
      <button
        className={`map-filter-btn ${filters.rich ? 'active rich' : ''}`}
        onClick={() => onFilterChange('rich', !filters.rich)}
        title="Highlight $300K+ properties"
      >
        💰 Rich {stats.rich > 0 && <span className="filter-count">{stats.rich}</span>}
      </button>
      <button
        className={`map-filter-btn ${filters.hot ? 'active hot' : ''}`}
        onClick={() => onFilterChange('hot', !filters.hot)}
        title="Show only hot leads"
      >
        🔥 Hot {stats.hot > 0 && <span className="filter-count">{stats.hot}</span>}
      </button>
      <button
        className={`map-filter-btn ${filters.bigRoof ? 'active bigroof' : ''}`}
        onClick={() => onFilterChange('bigRoof', !filters.bigRoof)}
        title="Highlight big roofs (2500+ sqft)"
      >
        🏠 Big Roof {stats.bigRoof > 0 && <span className="filter-count">{stats.bigRoof}</span>}
      </button>
    </div>
  );
}

function MapComponent({
  center,
  initialZoom = 17,
  drawingMode,
  setDrawingMode,
  onLeadsFound,
  setIsLoading,
  mapRef,
  onLocateMe,
  currentStep,
  setCurrentStep,
  trackApiCall,
  selectedLead,
  onMapIdle,
  dbLeads = [],
  leads = [], // Current session leads (from hotspots or drawing)
  selectedLeadIndices = new Set(),
  onPropertyClick,
  onLeadClick, // Click handler for session leads
  // Storm data props
  storms = [],
  showStormOverlay = true,
  getStormColor,
  getStormRadius
}) {
  const [currentShape, setCurrentShape] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [mapBounds, setMapBounds] = useState(null);
  const [selectedStorm, setSelectedStorm] = useState(null);
  const [stormImpactRadius, setStormImpactRadius] = useState(null); // Circle around selected storm
  const [darkMode, setDarkMode] = useState(true); // Default to dark storm tracker mode

  // Churches nearby
  const [showChurches, setShowChurches] = useState(false);
  const [churches, setChurches] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState(null);

  // Week route overlay state
  const [showWeekRoute, setShowWeekRoute] = useState(false);
  const [selectedRouteDay, setSelectedRouteDay] = useState(0);
  const [directionsResult, setDirectionsResult] = useState(null);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);

  // Calculate impact radius based on storm type (in meters)
  const getStormImpactRadius = useCallback((storm) => {
    if (!storm) return 0;
    if (storm.event_type === 'tornado') {
      // Tornado path width - typically 1/4 to 1/2 mile
      return 800; // ~0.5 miles
    } else if (storm.event_type === 'hail') {
      // Hail damage area - larger for bigger hail
      const sizeInches = (storm.magnitude || 100) / 100;
      if (sizeInches >= 2) return 3200; // 2 miles for golf ball+
      if (sizeInches >= 1) return 2400; // 1.5 miles for 1"+
      return 1600; // 1 mile for smaller hail
    } else {
      // Wind damage - typically localized
      return 1200; // ~0.75 miles
    }
  }, []);

  // Map filters for sales planning
  const [mapFilters, setMapFilters] = useState({
    storms: true,    // Show storm overlay
    rich: false,     // Highlight rich properties
    hot: false,      // Show only hot leads
    bigRoof: false   // Highlight big roofs
  });

  const handleFilterChange = useCallback((filter, value) => {
    setMapFilters(prev => ({ ...prev, [filter]: value }));
  }, []);

  // Combine API storms with local storm data (no state filter - show all like StormerSite)
  const allStorms = useMemo(() => {
    // Normalize local storm data to match API format
    const normalizedLocalStorms = INDIANA_STORM_DATA.map((s, i) => ({
      id: `local-${i}`,
      event_date: s.date,
      event_type: s.type,
      latitude: s.lat,
      longitude: s.lng,
      magnitude: s.magnitude,
      county: s.county,
      state: s.state,
      comments: s.description
    }));

    // Combine: use local data if no API storms, otherwise merge (dedupe by location)
    if (storms.length === 0) {
      return normalizedLocalStorms;
    }

    // Merge: add local storms that aren't already in API data (within 0.01 degrees)
    const combined = [...storms];
    normalizedLocalStorms.forEach(local => {
      const exists = storms.some(api =>
        Math.abs(api.latitude - local.latitude) < 0.01 &&
        Math.abs(api.longitude - local.longitude) < 0.01 &&
        api.event_type === local.event_type
      );
      if (!exists) {
        combined.push(local);
      }
    });

    return combined;
  }, [storms]);

  // Calculate filter stats
  const filterStats = useMemo(() => {
    const allLeads = [...leads, ...dbLeads];
    return {
      storms: allStorms.length,
      rich: allLeads.filter(l => (l.assessedValue || l.assessed_value || 0) >= 300000).length,
      hot: allLeads.filter(l => l.leadQuality === 'hot').length,
      bigRoof: allLeads.filter(l => (l.roofSqft || l.estRoofSqft || l.roof_sqft || 0) >= 2500).length
    };
  }, [leads, dbLeads, allStorms]);

  // Custom circle drawing state
  const isDrawingCircle = useRef(false);
  const circleStartPoint = useRef(null);
  const previewCircle = useRef(null);

  // Lasso (freeform) drawing state
  const isDrawingLasso = useRef(false);
  const lassoPoints = useRef([]);
  const previewPolyline = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;

    // Track map load for FinOps
    trackMapsUsage('map_load', 1);

    // Set initial zoom and bounds
    setCurrentZoom(map.getZoom());
    setMapBounds(map.getBounds());

    // Listen for zoom changes
    map.addListener('zoom_changed', () => {
      setCurrentZoom(map.getZoom());
    });

    // Listen for bounds changes (for clustering visibility)
    map.addListener('bounds_changed', () => {
      setMapBounds(map.getBounds());
    });
  }, [mapRef]);

  // Search for churches nearby when toggled on or map moves
  const searchChurches = useCallback(() => {
    if (!showChurches || !mapRef.current) return;
    const map = mapRef.current;
    const service = new window.google.maps.places.PlacesService(map);
    const center = map.getCenter();
    service.nearbySearch({
      location: { lat: center.lat(), lng: center.lng() },
      radius: 8000, // 5 miles
      type: 'church'
    }, (results, status) => {
      trackMapsUsage('places_nearby', 1);
      if (status === 'OK' && results) {
        setChurches(results.map(r => ({
          name: r.name,
          lat: r.geometry.location.lat(),
          lng: r.geometry.location.lng(),
          address: r.vicinity,
          rating: r.rating,
          placeId: r.place_id
        })));
      }
    });
  }, [showChurches, mapRef]);

  useEffect(() => {
    if (showChurches) searchChurches();
    else setChurches([]);
  }, [showChurches, searchChurches]);

  // Compute clusters for session leads
  const { clusters: leadClusters, unclustered: unclusteredLeads } = useMemo(() => {
    const result = clusterLeads(leads, mapBounds, currentZoom);
    if (leads.length > 0) {
      console.log(`📍 Clustering: ${leads.length} leads → ${result.clusters.length} clusters, ${result.unclustered.length} individual markers (zoom: ${currentZoom})`);
    }
    return result;
  }, [leads, mapBounds, currentZoom]);

  // Generate heatmap data from hotspot zones and storms
  const heatmapData = useMemo(() => {
    if (!window.google?.maps?.LatLng) return [];

    const data = [];

    // Add hotspot zones to heatmap (always visible)
    HOTSPOT_ZONES.forEach(zone => {
      // Create multiple points around each zone for better spread
      const weight = zone.stormPriority ? 8 : (zone.wealthy ? 5 : 3);
      // Center point
      data.push({
        location: new window.google.maps.LatLng(zone.lat, zone.lng),
        weight: weight * 2
      });
      // Surrounding points for spread effect
      const spread = 0.015;
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * 2 * Math.PI;
        data.push({
          location: new window.google.maps.LatLng(
            zone.lat + Math.sin(angle) * spread,
            zone.lng + Math.cos(angle) * spread
          ),
          weight: weight
        });
      }
    });

    // Add storm events to heatmap
    if (storms && storms.length > 0) {
      storms.forEach(storm => {
        // Weight based on magnitude (hail size, wind speed)
        let weight = 10;
        if (storm.magnitude) {
          weight = Math.min(20, storm.magnitude * 8); // Larger hail = higher weight
        }
        data.push({
          location: new window.google.maps.LatLng(storm.latitude, storm.longitude),
          weight: weight
        });
        // Add surrounding points for better visibility
        const spread = 0.02;
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * 2 * Math.PI;
          data.push({
            location: new window.google.maps.LatLng(
              storm.latitude + Math.sin(angle) * spread,
              storm.longitude + Math.cos(angle) * spread
            ),
            weight: weight * 0.5
          });
        }
      });
    }

    console.log(`🌡️ Heatmap: ${data.length} points (${HOTSPOT_ZONES.length} zones, ${storms?.length || 0} storms)`);
    return data;
  }, [storms]);

  // Handle cluster click - zoom in to show individual markers
  const handleClusterClick = useCallback((cluster) => {
    if (!mapRef.current) return;

    // Calculate bounds that contains all leads in the cluster
    const bounds = new window.google.maps.LatLngBounds();
    cluster.leads.forEach(lead => {
      bounds.extend({ lat: lead.lat, lng: lead.lng });
    });

    // Fit map to show all leads in cluster with some padding
    mapRef.current.fitBounds(bounds, { padding: 50 });

    // Ensure we zoom in enough to see individual markers
    const newZoom = mapRef.current.getZoom();
    if (newZoom < 16) {
      mapRef.current.setZoom(16);
    }
  }, [mapRef]);

  // Disable map dragging when in drawing mode
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({
        draggable: !drawingMode,
        scrollwheel: !drawingMode,
        disableDoubleClickZoom: !!drawingMode
      });
    }
  }, [drawingMode, mapRef]);

  // Center and zoom on selected lead
  useEffect(() => {
    console.log('=== Selected Lead Changed ===');
    console.log('selectedLead:', selectedLead?.streetAddress, 'lat:', selectedLead?.lat, 'lng:', selectedLead?.lng);
    if (selectedLead && selectedLead.lat && selectedLead.lng && mapRef.current) {
      console.log('Panning to lead location');
      mapRef.current.panTo({ lat: selectedLead.lat, lng: selectedLead.lng });
      mapRef.current.setZoom(19); // Close zoom to see the house
    }
  }, [selectedLead, mapRef]);

  // Fetch driving directions for the selected week route day
  useEffect(() => {
    if (!showWeekRoute) {
      setDirectionsResult(null);
      return;
    }
    if (!window.google?.maps?.DirectionsService) return;

    const plan = WEEK_PLAN[selectedRouteDay];
    if (plan.stops.length < 2) return;

    const directionsService = new window.google.maps.DirectionsService();
    const origin = plan.stops[0].address;
    const destination = plan.stops[plan.stops.length - 1].address;
    const waypoints = plan.stops.slice(1, -1).map(stop => ({
      location: stop.address,
      stopover: true
    }));

    setIsLoadingRoute(true);
    directionsService.route({
      origin,
      destination,
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false
    }, (result, status) => {
      setIsLoadingRoute(false);
      if (status === 'OK') {
        setDirectionsResult(result);
        if (mapRef.current) {
          mapRef.current.fitBounds(result.routes[0].bounds, { padding: 60 });
        }
      } else {
        console.warn('Directions request failed:', status);
      }
    });
  }, [showWeekRoute, selectedRouteDay, mapRef]);

  const clearShape = useCallback(() => {
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
    }
    onLeadsFound([]);
    setDrawingMode(null);
    setCurrentStep(2);
  }, [currentShape, onLeadsFound, setDrawingMode, setCurrentStep]);

  const getAddressesInBounds = useCallback(async (bounds) => {
    setIsLoading(true);

    const geocoder = new window.google.maps.Geocoder();
    const addresses = [];

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Calculate area size in meters for adaptive grid density
    const latDist = Math.abs(ne.lat() - sw.lat()) * 111320; // ~111km per degree
    const lngDist = Math.abs(ne.lng() - sw.lng()) * 111320 * Math.cos(((ne.lat() + sw.lat()) / 2) * Math.PI / 180);
    const areaSqMeters = latDist * lngDist;

    // Adaptive grid: denser for smaller areas to ensure we catch individual houses
    // Typical residential lot is ~500-1000 sq meters, house footprint ~150-300 sq meters
    let gridSize;
    if (areaSqMeters < 500) { // Single house
      gridSize = 3; // 9 points - more likely to hit center
    } else if (areaSqMeters < 1500) { // 1-2 houses
      gridSize = 4; // 16 points
    } else if (areaSqMeters < 4000) { // 3-5 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 10000) { // Small: 5-10 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 25000) { // Medium: 10-25 houses
      gridSize = 6; // 36 points
    } else if (areaSqMeters < 60000) { // Large: neighborhood block
      gridSize = 7; // 49 points
    } else if (areaSqMeters < 200000) { // Very large
      gridSize = 8; // 64 points
    } else {
      gridSize = 10; // Huge area: 100 points
    }

    // Calculate step to get exactly gridSize points per dimension
    // We want gridSize points spread across the area (including edges)
    const latStep = gridSize > 1 ? (ne.lat() - sw.lat()) / (gridSize - 1) : 0;
    const lngStep = gridSize > 1 ? (ne.lng() - sw.lng()) / (gridSize - 1) : 0;

    console.log('=== Geocoding Debug ===');
    console.log('Bounds:', { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } });
    console.log('Area:', Math.round(areaSqMeters), 'sq meters, Grid:', gridSize, 'x', gridSize, '=', gridSize * gridSize, 'points');

    const promises = [];

    // Generate exactly gridSize x gridSize sample points
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const lat = sw.lat() + i * latStep;
        const lng = sw.lng() + j * lngStep;
        const location = { lat, lng };

        promises.push(
          new Promise((resolve) => {
            geocoder.geocode({ location }, (results, status) => {
              // Track API call for FinOps
              trackMapsUsage('geocode', 1);
              if (trackApiCall) trackApiCall('geocoding');

              if (status === 'OK' && results && results.length > 0) {
                // Only accept ROOFTOP precision (actual addresses, not interpolated)
                // RANGE_INTERPOLATED = Google guessed the address (fake!)
                // ROOFTOP = Actual verified address location
                const streetAddresses = results.filter(r =>
                  (r.types.includes('street_address') ||
                   r.types.includes('premise') ||
                   r.types.includes('subpremise')) &&
                  r.geometry.location_type === 'ROOFTOP'
                );

                streetAddresses.forEach(result => {
                  const components = result.address_components;
                  const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
                  const route = components.find(c => c.types.includes('route'))?.long_name || '';
                  const city = components.find(c => c.types.includes('locality'))?.long_name ||
                              components.find(c => c.types.includes('sublocality'))?.long_name || '';
                  const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                  const zip = components.find(c => c.types.includes('postal_code'))?.long_name || '';

                  const addrLat = result.geometry.location.lat();
                  const addrLng = result.geometry.location.lng();

                  // Only include if address is actually INSIDE the drawn bounds
                  const isInsideBounds =
                    addrLat >= sw.lat() && addrLat <= ne.lat() &&
                    addrLng >= sw.lng() && addrLng <= ne.lng();

                  if (streetNumber && route && isInsideBounds) {
                    addresses.push({
                      streetAddress: `${streetNumber} ${route}`,
                      city,
                      state,
                      zip,
                      fullAddress: result.formatted_address,
                      lat: addrLat,
                      lng: addrLng,
                      placeId: result.place_id
                    });
                  }
                });
              }
              resolve();
            });
          })
        );
      }
    }

    await Promise.all(promises);

    // Deduplicate by street address (not placeId since same address can have multiple placeIds)
    const uniqueAddresses = addresses.filter((addr, index, self) =>
      index === self.findIndex(a => a.streetAddress === addr.streetAddress)
    );

    // Sort by street number numerically
    uniqueAddresses.sort((a, b) => {
      const numA = parseInt(a.streetAddress) || 0;
      const numB = parseInt(b.streetAddress) || 0;
      return numA - numB;
    });

    console.log('Raw addresses found:', addresses.length);
    console.log('Unique addresses:', uniqueAddresses.length);
    console.log('Addresses:', uniqueAddresses.map(a => a.streetAddress));

    setIsLoading(false);
    onLeadsFound(uniqueAddresses);
  }, [onLeadsFound, setIsLoading, trackApiCall]);

  // Check if a point is inside a polygon using ray casting
  const isPointInPolygon = useCallback((point, polygon) => {
    const path = polygon.getPath().getArray();
    let inside = false;
    const x = point.lng, y = point.lat;

    for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
      const xi = path[i].lng(), yi = path[i].lat();
      const xj = path[j].lng(), yj = path[j].lat();

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  // Get addresses within a polygon (lasso)
  const getAddressesInPolygon = useCallback(async (polygon) => {
    setIsLoading(true);

    const geocoder = new window.google.maps.Geocoder();
    const addresses = [];

    // Get bounding box of polygon
    const bounds = new window.google.maps.LatLngBounds();
    polygon.getPath().forEach(point => bounds.extend(point));

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Calculate area size in meters for adaptive grid density
    const latDist = Math.abs(ne.lat() - sw.lat()) * 111320;
    const lngDist = Math.abs(ne.lng() - sw.lng()) * 111320 * Math.cos(((ne.lat() + sw.lat()) / 2) * Math.PI / 180);
    const areaSqMeters = latDist * lngDist;

    // Adaptive grid: denser for smaller areas to ensure we catch individual houses
    let gridSize;
    if (areaSqMeters < 500) { // Single house
      gridSize = 3; // 9 points - more likely to hit center
    } else if (areaSqMeters < 1500) { // 1-2 houses
      gridSize = 4; // 16 points
    } else if (areaSqMeters < 4000) { // 3-5 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 10000) { // Small: 5-10 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 25000) { // Medium: 10-25 houses
      gridSize = 6; // 36 points
    } else if (areaSqMeters < 60000) { // Large: neighborhood block
      gridSize = 7; // 49 points
    } else if (areaSqMeters < 200000) { // Very large
      gridSize = 8; // 64 points
    } else {
      gridSize = 10; // Huge area: 100 points
    }

    // Calculate step to get exactly gridSize points per dimension
    const latStep = gridSize > 1 ? (ne.lat() - sw.lat()) / (gridSize - 1) : 0;
    const lngStep = gridSize > 1 ? (ne.lng() - sw.lng()) / (gridSize - 1) : 0;

    console.log('=== Lasso Geocoding Debug ===');
    console.log('Polygon bounds:', { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } });
    console.log('Area:', Math.round(areaSqMeters), 'sq meters, Grid:', gridSize, 'x', gridSize, '=', gridSize * gridSize, 'points');

    const promises = [];

    // Generate exactly gridSize x gridSize sample points
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const lat = sw.lat() + i * latStep;
        const lng = sw.lng() + j * lngStep;

        // Only geocode points inside the polygon
        if (!isPointInPolygon({ lat, lng }, polygon)) continue;

        const location = { lat, lng };

        promises.push(
          new Promise((resolve) => {
            geocoder.geocode({ location }, (results, status) => {
              // Track API call for FinOps
              trackMapsUsage('geocode', 1);
              if (trackApiCall) trackApiCall('geocoding');

              if (status === 'OK' && results && results.length > 0) {
                // Only accept ROOFTOP precision (actual addresses, not interpolated)
                // RANGE_INTERPOLATED = Google guessed the address (fake!)
                // ROOFTOP = Actual verified address location
                const streetAddresses = results.filter(r =>
                  (r.types.includes('street_address') ||
                   r.types.includes('premise') ||
                   r.types.includes('subpremise')) &&
                  r.geometry.location_type === 'ROOFTOP'
                );

                streetAddresses.forEach(result => {
                  const components = result.address_components;
                  const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
                  const route = components.find(c => c.types.includes('route'))?.long_name || '';
                  const city = components.find(c => c.types.includes('locality'))?.long_name ||
                              components.find(c => c.types.includes('sublocality'))?.long_name || '';
                  const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                  const zip = components.find(c => c.types.includes('postal_code'))?.long_name || '';

                  const addrLat = result.geometry.location.lat();
                  const addrLng = result.geometry.location.lng();

                  // Only include if address is actually INSIDE the polygon
                  const isInsidePolygon = isPointInPolygon({ lat: addrLat, lng: addrLng }, polygon);

                  if (streetNumber && route && isInsidePolygon) {
                    addresses.push({
                      streetAddress: `${streetNumber} ${route}`,
                      city,
                      state,
                      zip,
                      fullAddress: result.formatted_address,
                      lat: addrLat,
                      lng: addrLng,
                      placeId: result.place_id
                    });
                  }
                });
              }
              resolve();
            });
          })
        );
      }
    }

    await Promise.all(promises);

    // Deduplicate by street address
    const uniqueAddresses = addresses.filter((addr, index, self) =>
      index === self.findIndex(a => a.streetAddress === addr.streetAddress)
    );

    // Sort by street number numerically
    uniqueAddresses.sort((a, b) => {
      const numA = parseInt(a.streetAddress) || 0;
      const numB = parseInt(b.streetAddress) || 0;
      return numA - numB;
    });

    console.log('Unique addresses in polygon:', uniqueAddresses.length);

    setIsLoading(false);
    onLeadsFound(uniqueAddresses);
  }, [onLeadsFound, setIsLoading, isPointInPolygon, trackApiCall]);

  const onPolygonComplete = useCallback((polygon) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(polygon);
    setDrawingMode(null);

    getAddressesInPolygon(polygon);
  }, [currentShape, setDrawingMode, getAddressesInPolygon]);

  const onCircleComplete = useCallback((circle) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(circle);
    setDrawingMode(null);

    const bounds = circle.getBounds();
    getAddressesInBounds(bounds);

    circle.addListener('radius_changed', () => {
      getAddressesInBounds(circle.getBounds());
    });
    circle.addListener('center_changed', () => {
      getAddressesInBounds(circle.getBounds());
    });
  }, [currentShape, setDrawingMode, getAddressesInBounds]);

  const onRectangleComplete = useCallback((rectangle) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(rectangle);
    setDrawingMode(null);

    const bounds = rectangle.getBounds();
    getAddressesInBounds(bounds);

    rectangle.addListener('bounds_changed', () => {
      getAddressesInBounds(rectangle.getBounds());
    });
  }, [currentShape, setDrawingMode, getAddressesInBounds]);

  const handleDrawClick = (mode) => {
    // Clear existing shape when entering drawing mode
    if (mode && currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }
    setDrawingMode(drawingMode === mode ? null : mode);
    if (currentStep === 1) setCurrentStep(2);
  };

  // Lasso (freeform) drawing handlers
  const startLassoDrawing = useCallback((latLng) => {
    if (drawingMode !== 'lasso') return;

    // Clear existing shape and data
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }

    isDrawingLasso.current = true;
    lassoPoints.current = [{ lat: latLng.lat(), lng: latLng.lng() }];

    // Create preview polyline
    if (previewPolyline.current) {
      previewPolyline.current.setMap(null);
    }

    previewPolyline.current = new window.google.maps.Polyline({
      map: mapRef.current,
      path: lassoPoints.current,
      strokeColor: '#3b82f6',
      strokeWeight: 3,
      strokeOpacity: 0.8
    });
  }, [drawingMode, mapRef, currentShape, onLeadsFound]);

  const updateLassoDrawing = useCallback((latLng) => {
    if (!isDrawingLasso.current || !previewPolyline.current) return;

    lassoPoints.current.push({ lat: latLng.lat(), lng: latLng.lng() });
    previewPolyline.current.setPath(lassoPoints.current);
  }, []);

  const finishLassoDrawing = useCallback(() => {
    if (!isDrawingLasso.current) return;

    isDrawingLasso.current = false;

    // Remove preview polyline
    if (previewPolyline.current) {
      previewPolyline.current.setMap(null);
      previewPolyline.current = null;
    }

    // Need at least 3 points for a polygon
    if (lassoPoints.current.length < 3) {
      lassoPoints.current = [];
      return;
    }

    // Simplify path (reduce points for better performance)
    // Keep more points for small shapes, fewer for large ones
    const skipFactor = lassoPoints.current.length > 50 ? 4 :
                       lassoPoints.current.length > 20 ? 3 :
                       lassoPoints.current.length > 10 ? 2 : 1;
    const simplifiedPath = lassoPoints.current.filter((_, i) =>
      i % skipFactor === 0 || i === lassoPoints.current.length - 1
    );

    // Create final polygon
    const polygon = new window.google.maps.Polygon({
      map: mapRef.current,
      paths: simplifiedPath,
      ...drawingOptions.polygonOptions
    });

    lassoPoints.current = [];
    onPolygonComplete(polygon);
  }, [mapRef, onPolygonComplete]);

  // Custom circle drawing - drag from corner to corner like rectangle
  // Works with both mouse (desktop) and touch (mobile)
  const startCircleDrawing = useCallback((latLng) => {
    if (drawingMode !== 'circle') return;

    // Clear existing shape and data when starting new drawing
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }

    isDrawingCircle.current = true;
    circleStartPoint.current = {
      lat: latLng.lat(),
      lng: latLng.lng()
    };

    // Create preview circle
    if (previewCircle.current) {
      previewCircle.current.setMap(null);
    }

    previewCircle.current = new window.google.maps.Circle({
      map: mapRef.current,
      center: circleStartPoint.current,
      radius: 1,
      fillColor: '#3b82f6',
      fillOpacity: 0.2,
      strokeWeight: 2,
      strokeColor: '#3b82f6',
      clickable: false,
      editable: false,
      draggable: false,
      zIndex: 1
    });
  }, [drawingMode, mapRef, currentShape, onLeadsFound]);

  // Update circle preview during drag (mouse or touch)
  const updateCircleDrawing = useCallback((latLng) => {
    if (!isDrawingCircle.current || !circleStartPoint.current || !previewCircle.current) return;

    const start = circleStartPoint.current;
    const endLat = latLng.lat();
    const endLng = latLng.lng();

    // Calculate center as midpoint between start and end
    const centerLat = (start.lat + endLat) / 2;
    const centerLng = (start.lng + endLng) / 2;

    // Calculate radius as half the diagonal distance
    const latDiff = Math.abs(endLat - start.lat);
    const lngDiff = Math.abs(endLng - start.lng);

    // Convert to meters (approximate)
    const latMeters = latDiff * 111320;
    const lngMeters = lngDiff * 111320 * Math.cos(centerLat * Math.PI / 180);

    // Use half the diagonal for radius
    const radius = Math.sqrt(latMeters * latMeters + lngMeters * lngMeters) / 2;

    // Batch updates for better performance
    previewCircle.current.setOptions({
      center: { lat: centerLat, lng: centerLng },
      radius: radius
    });
  }, []);

  // Finish circle drawing (mouse or touch)
  const finishCircleDrawing = useCallback((latLng) => {
    if (!isDrawingCircle.current || !circleStartPoint.current) return;

    isDrawingCircle.current = false;

    const start = circleStartPoint.current;
    const endLat = latLng.lat();
    const endLng = latLng.lng();

    // Calculate final circle
    const centerLat = (start.lat + endLat) / 2;
    const centerLng = (start.lng + endLng) / 2;

    const latDiff = Math.abs(endLat - start.lat);
    const lngDiff = Math.abs(endLng - start.lng);

    const latMeters = latDiff * 111320;
    const lngMeters = lngDiff * 111320 * Math.cos(centerLat * Math.PI / 180);
    const radius = Math.sqrt(latMeters * latMeters + lngMeters * lngMeters) / 2;

    // Remove preview
    if (previewCircle.current) {
      previewCircle.current.setMap(null);
      previewCircle.current = null;
    }

    // Don't create if too small
    if (radius < 10) {
      circleStartPoint.current = null;
      return;
    }

    // Create final editable circle
    const circle = new window.google.maps.Circle({
      map: mapRef.current,
      center: { lat: centerLat, lng: centerLng },
      radius: radius,
      ...drawingOptions.circleOptions
    });

    circleStartPoint.current = null;

    // Use existing onCircleComplete handler
    onCircleComplete(circle);
  }, [mapRef, onCircleComplete]);

  // Mouse and Touch event support for lasso/circle drawing
  // Using native DOM events for reliable continuous drawing
  useEffect(() => {
    if (!mapRef.current || (drawingMode !== 'circle' && drawingMode !== 'lasso')) return;

    const map = mapRef.current;
    const mapDiv = map.getDiv();

    const getLatLngFromMouse = (e) => {
      const rect = mapDiv.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const bounds = map.getBounds();
      if (!bounds) return null;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const lng = sw.lng() + (x / rect.width) * (ne.lng() - sw.lng());
      const lat = ne.lat() - (y / rect.height) * (ne.lat() - sw.lat());

      return {
        lat: () => lat,
        lng: () => lng
      };
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0) return; // Left click only
      e.preventDefault();

      const latLng = getLatLngFromMouse(e);
      if (!latLng) return;

      if (drawingMode === 'circle') {
        startCircleDrawing(latLng);
      } else if (drawingMode === 'lasso') {
        startLassoDrawing(latLng);
      }
    };

    const handleMouseMove = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      const latLng = getLatLngFromMouse(e);
      if (!latLng) return;

      if (isDrawingCircle.current) {
        updateCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        updateLassoDrawing(latLng);
      }
    };

    const handleMouseUp = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      if (isDrawingCircle.current) {
        const latLng = getLatLngFromMouse(e);
        if (latLng) finishCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        finishLassoDrawing();
      }
    };

    // Add mouse listeners
    mapDiv.addEventListener('mousedown', handleMouseDown);
    mapDiv.addEventListener('mousemove', handleMouseMove);
    mapDiv.addEventListener('mouseup', handleMouseUp);
    mapDiv.addEventListener('mouseleave', handleMouseUp); // Finish if mouse leaves map

    const getLatLngFromTouch = (touch) => {
      const rect = mapDiv.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Convert pixel coordinates to lat/lng
      const projection = map.getProjection();
      if (!projection) return null;

      const bounds = map.getBounds();
      if (!bounds) return null;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const lng = sw.lng() + (x / rect.width) * (ne.lng() - sw.lng());
      const lat = ne.lat() - (y / rect.height) * (ne.lat() - sw.lat());

      return {
        lat: () => lat,
        lng: () => lng
      };
    };

    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();

      const latLng = getLatLngFromTouch(e.touches[0]);
      if (!latLng) return;

      if (drawingMode === 'circle') {
        startCircleDrawing(latLng);
      } else if (drawingMode === 'lasso') {
        startLassoDrawing(latLng);
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      const latLng = getLatLngFromTouch(e.touches[0]);
      if (!latLng) return;

      if (isDrawingCircle.current) {
        updateCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        updateLassoDrawing(latLng);
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      if (isDrawingCircle.current) {
        const latLng = getLatLngFromTouch(e.changedTouches[0]);
        if (latLng) finishCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        finishLassoDrawing();
      }
    };

    // Add touch listeners with passive: false to allow preventDefault
    mapDiv.addEventListener('touchstart', handleTouchStart, { passive: false });
    mapDiv.addEventListener('touchmove', handleTouchMove, { passive: false });
    mapDiv.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      // Remove mouse listeners
      mapDiv.removeEventListener('mousedown', handleMouseDown);
      mapDiv.removeEventListener('mousemove', handleMouseMove);
      mapDiv.removeEventListener('mouseup', handleMouseUp);
      mapDiv.removeEventListener('mouseleave', handleMouseUp);
      // Remove touch listeners
      mapDiv.removeEventListener('touchstart', handleTouchStart);
      mapDiv.removeEventListener('touchmove', handleTouchMove);
      mapDiv.removeEventListener('touchend', handleTouchEnd);
    };
  }, [drawingMode, mapRef, startCircleDrawing, updateCircleDrawing, finishCircleDrawing, startLassoDrawing, updateLassoDrawing, finishLassoDrawing]);

  return (
    <>
      {/* Storm Alert Banner - shows when storms are loaded */}
      {allStorms.length > 0 && mapFilters.storms && (
        <div className="storm-alert-banner">
          <span className="storm-alert-icon">⚡</span>
          <span>{allStorms.length} Indiana storm zone{allStorms.length > 1 ? 's' : ''}</span>
          <button
            className="storm-alert-close"
            onClick={() => handleFilterChange('storms', false)}
          >
            Hide
          </button>
        </div>
      )}

      {/* Drawing Controls + Filters (inline scrollable on mobile) */}
      <div className="drawing-controls">
        <MapFilters
          filters={mapFilters}
          onFilterChange={handleFilterChange}
          stats={filterStats}
        />
        <button
          className={`draw-btn ${drawingMode === 'circle' ? 'active' : ''}`}
          onClick={() => handleDrawClick('circle')}
        >
          <div className="draw-btn-icon">
            <img src="/images/icon-circle.webp" alt="Circle" />
          </div>
          <span>Circle</span>
        </button>

        <button
          className={`draw-btn ${drawingMode === 'rectangle' ? 'active' : ''}`}
          onClick={() => handleDrawClick('rectangle')}
        >
          <div className="draw-btn-icon">
            <img src="/images/icon-rectangle.webp" alt="Rectangle" />
          </div>
          <span>Rectangle</span>
        </button>

        <button
          className={`draw-btn ${drawingMode === 'lasso' ? 'active' : ''}`}
          onClick={() => handleDrawClick('lasso')}
        >
          <div className="draw-btn-icon">
            <span style={{ fontSize: '1.25rem' }}>✏️</span>
          </div>
          <span>Lasso</span>
        </button>

        <button className="draw-btn" onClick={onLocateMe}>
          <div className="draw-btn-icon">
            <img src="/images/icon-location.webp" alt="Location" />
          </div>
          <span>My Location</span>
        </button>

        <button
          className={`draw-btn ${darkMode ? 'active' : ''}`}
          onClick={() => setDarkMode(!darkMode)}
          title={darkMode ? 'Switch to Satellite View' : 'Switch to Storm Tracker View'}
        >
          <div className="draw-btn-icon">
            <span style={{ fontSize: '1.25rem' }}>{darkMode ? '🌙' : '🛰️'}</span>
          </div>
          <span>{darkMode ? 'Storm' : 'Satellite'}</span>
        </button>

        {currentShape && (
          <button className="draw-btn clear" onClick={clearShape}>
            <div className="draw-btn-icon">
              <span>🗑️</span>
            </div>
            <span>Clear</span>
          </button>
        )}

        <button
          className={`draw-btn ${showWeekRoute ? 'active' : ''}`}
          onClick={() => {
            const next = !showWeekRoute;
            setShowWeekRoute(next);
            if (!next) setDirectionsResult(null);
          }}
          title="Show Week Route Plan"
        >
          <div className="draw-btn-icon">
            <span style={{ fontSize: '1.25rem' }}>📅</span>
          </div>
          <span>Route</span>
        </button>

        <button
          className={`draw-btn ${showChurches ? 'active' : ''}`}
          onClick={() => setShowChurches(!showChurches)}
          title="Show churches nearby"
        >
          <div className="draw-btn-icon">
            <span style={{ fontSize: '1.25rem' }}>⛪</span>
          </div>
          <span>Churches</span>
        </button>
      </div>

      {/* Week Route Day Selector */}
      {showWeekRoute && (
        <div className="route-day-selector">
          {WEEK_PLAN.map((plan, idx) => (
            <button
              key={idx}
              className={`route-day-btn ${selectedRouteDay === idx ? 'active' : ''}`}
              style={selectedRouteDay === idx ? { background: plan.color, borderColor: plan.color, color: '#fff' } : {}}
              onClick={() => { setSelectedRouteDay(idx); setDirectionsResult(null); }}
            >
              <span className="route-day-label">{plan.day}</span>
              <span className="route-day-potential">{plan.stats.potential}</span>
            </button>
          ))}
          {isLoadingRoute && <span style={{ color: '#f97316', fontSize: '12px', alignSelf: 'center', marginLeft: 8 }}>📍 Loading…</span>}
        </div>
      )}

      {/* Map */}
      <GoogleMap
        mapContainerStyle={getMapContainerStyle(!!drawingMode)}
        center={center}
        zoom={initialZoom}
        onLoad={onLoad}
        onIdle={onMapIdle}
        options={getMapOptions(!!drawingMode, darkMode)}
      >
        {/* Only use DrawingManager for rectangle, circle is custom */}
        {drawingMode === 'rectangle' && (
          <DrawingManager
            drawingMode={window.google.maps.drawing.OverlayType.RECTANGLE}
            options={{
              drawingControl: false,
              rectangleOptions: drawingOptions.rectangleOptions
            }}
            onRectangleComplete={(rect) => {
              console.log('Rectangle drawn', rect);
              onRectangleComplete(rect);
            }}
          />
        )}

        {/* DB leads - Price markers on map (like Redfin) */}
        {dbLeads.filter(l => l.lat && l.lng).map((lead, index) => (
          <OverlayView
            key={`db-${lead.id || index}`}
            position={{ lat: lead.lat, lng: lead.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <PriceMarker
              lead={lead}
              onClick={onPropertyClick}
              isSelected={selectedLead?.id === lead.id}
            />
          </OverlayView>
        ))}

        {/* Session leads - Cluster markers when zoomed out */}
        {leadClusters.map((cluster, index) => (
          <OverlayView
            key={`cluster-${index}`}
            position={cluster.center}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <ClusterMarker
              cluster={cluster}
              onClick={handleClusterClick}
            />
          </OverlayView>
        ))}

        {/* Session leads - Individual price markers when zoomed in */}
        {unclusteredLeads.map((lead, index) => {
          // Find original index in leads array for selection tracking
          const originalIndex = leads.findIndex(l =>
            l.streetAddress === lead.streetAddress && l.zip === lead.zip
          );
          return (
            <OverlayView
              key={`lead-${lead.placeId || index}`}
              position={{ lat: lead.lat, lng: lead.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <PriceMarker
                lead={lead}
                onClick={() => onLeadClick && onLeadClick(lead, originalIndex >= 0 ? originalIndex : index)}
                isSelected={selectedLeadIndices.has(originalIndex) || selectedLead?.streetAddress === lead.streetAddress}
              />
            </OverlayView>
          );
        })}

        {/* Zone markers for hotspot areas - visible at city zoom */}
        {currentZoom < 12 && HOTSPOT_ZONES.map((zone) => (
          <OverlayView
            key={`zone-${zone.zip}`}
            position={{ lat: zone.lat, lng: zone.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <ZoneMarker
              zone={zone}
              showStorms={mapFilters.storms}
              showWealth={mapFilters.rich}
              onClick={(z) => {
                if (mapRef.current) {
                  mapRef.current.panTo({ lat: z.lat, lng: z.lng });
                  mapRef.current.setZoom(14);
                }
              }}
            />
          </OverlayView>
        ))}

        {/* Zone circles for hotspot areas - subtle indicators */}
        {currentZoom < 12 && HOTSPOT_ZONES.map((zone) => {
          const isStormZone = zone.stormPriority && mapFilters.storms;
          const isWealthZone = zone.wealthy && mapFilters.rich;
          if (!isStormZone && !isWealthZone) return null;
          return (
            <Circle
              key={`zone-circle-${zone.zip}`}
              center={{ lat: zone.lat, lng: zone.lng }}
              radius={2000}
              options={{
                fillColor: isStormZone ? '#f97316' : '#22c55e',
                fillOpacity: 0.08,
                strokeColor: isStormZone ? '#f97316' : '#22c55e',
                strokeWeight: 1,
                strokeOpacity: 0.4,
                clickable: false,
                zIndex: 5
              }}
            />
          );
        })}

        {/* Storm markers - using custom local icons */}
        {mapFilters.storms && allStorms.length > 0 && allStorms.map((storm, index) => {
          // Get marker icon based on storm type (custom SVG pins in /public/markers/)
          let iconUrl;
          if (storm.event_type === 'hail') {
            iconUrl = '/markers/hail.svg';
          } else if (storm.event_type === 'wind') {
            iconUrl = '/markers/wind.svg';
          } else if (storm.event_type === 'tornado') {
            iconUrl = '/markers/tornado.svg';
          } else {
            iconUrl = '/markers/wind.svg';
          }

          // Scale size based on magnitude for hail
          const markerSize = storm.event_type === 'hail' && storm.magnitude >= 150 ? 40 :
                            storm.event_type === 'tornado' ? 44 : 32;

          return (
            <Marker
              key={`storm-${storm.id || index}`}
              position={{ lat: storm.latitude, lng: storm.longitude }}
              icon={{
                url: iconUrl,
                scaledSize: new window.google.maps.Size(markerSize, markerSize),
                anchor: new window.google.maps.Point(markerSize / 2, markerSize / 2)
              }}
              onClick={() => {
                setSelectedStorm(storm);
                setStormImpactRadius(getStormImpactRadius(storm));
                // Zoom to storm location
                if (mapRef.current) {
                  mapRef.current.panTo({ lat: storm.latitude, lng: storm.longitude });
                  mapRef.current.setZoom(14);
                }
              }}
              zIndex={storm.event_type === 'tornado' ? 2000 : storm.event_type === 'hail' ? 950 : 1500}
            />
          );
        })}

        {/* Storm Info Window - Enhanced with zone data */}
        {selectedStorm && (
          <InfoWindow
            position={{ lat: selectedStorm.latitude, lng: selectedStorm.longitude }}
            onCloseClick={() => { setSelectedStorm(null); setStormImpactRadius(null); }}
          >
            <div style={{ padding: '12px', minWidth: '220px', maxWidth: '280px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              {/* Header with icon and type */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px',
                paddingBottom: '8px',
                borderBottom: '2px solid ' + (selectedStorm.event_type === 'hail' ? '#0ea5e9' : selectedStorm.event_type === 'tornado' ? '#dc2626' : '#f97316')
              }}>
                <span style={{ fontSize: '24px' }}>
                  {selectedStorm.event_type === 'hail' ? '🧊' : selectedStorm.event_type === 'tornado' ? '🌪️' : '💨'}
                </span>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#111827' }}>
                    {selectedStorm.event_type?.toUpperCase()} {selectedStorm.event_type === 'tornado' ? 'DAMAGE' : 'STORM'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {selectedStorm.county} County, {selectedStorm.state}
                  </div>
                </div>
              </div>

              {/* Details */}
              <div style={{ fontSize: '13px', color: '#374151' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#6b7280' }}>Date:</span>
                  <span style={{ fontWeight: '500' }}>{new Date(selectedStorm.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>

                {selectedStorm.event_type === 'hail' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#6b7280' }}>Hail Size:</span>
                    <span style={{ fontWeight: '600', color: selectedStorm.magnitude >= 100 ? '#dc2626' : '#374151' }}>
                      {(selectedStorm.magnitude / 100).toFixed(2)}" diameter
                      {selectedStorm.magnitude >= 100 && ' (SEVERE)'}
                    </span>
                  </div>
                )}

                {selectedStorm.event_type === 'wind' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#6b7280' }}>Wind Speed:</span>
                    <span style={{ fontWeight: '600', color: selectedStorm.magnitude >= 70 ? '#dc2626' : '#374151' }}>
                      {selectedStorm.magnitude} mph
                      {selectedStorm.magnitude >= 70 && ' (DAMAGING)'}
                    </span>
                  </div>
                )}

                {selectedStorm.event_type === 'tornado' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#6b7280' }}>Rating:</span>
                    <span style={{ fontWeight: '600', color: '#dc2626' }}>
                      EF-{selectedStorm.magnitude || '?'}
                    </span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#6b7280' }}>Coordinates:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                    {selectedStorm.latitude.toFixed(4)}, {selectedStorm.longitude.toFixed(4)}
                  </span>
                </div>
              </div>

              {/* Comments/Description */}
              {selectedStorm.comments && (
                <div style={{
                  marginTop: '10px',
                  padding: '8px',
                  background: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#4b5563',
                  lineHeight: '1.4'
                }}>
                  {selectedStorm.comments}
                </div>
              )}

              {/* NOAA Report Link - always show (generate from date if not present) */}
              <div style={{
                marginTop: '10px',
                padding: '8px',
                background: '#ecfdf5',
                borderRadius: '6px',
                border: '1px solid #10b981'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#065f46', marginBottom: '6px' }}>
                  Official NOAA Report:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <a
                    href={selectedStorm.noaa_report || getNoaaReportUrl(selectedStorm.event_date)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '11px',
                      color: '#047857',
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    View SPC Storm Report
                  </a>
                  {selectedStorm.nws_report && (
                    <a
                      href={selectedStorm.nws_report}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '11px',
                        color: '#047857',
                        textDecoration: 'underline',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      NWS Event Summary
                    </a>
                    )}
                  </div>
                  {selectedStorm.source && (
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                      Source: {selectedStorm.source}
                    </div>
                  )}
                </div>

              {/* Find Leads Button */}
              <button
                onClick={() => {
                  // Create a circle shape around the storm for lead search
                  if (currentShape) {
                    currentShape.setMap(null);
                  }
                  const radius = stormImpactRadius || getStormImpactRadius(selectedStorm);
                  const circle = new window.google.maps.Circle({
                    map: mapRef.current,
                    center: { lat: selectedStorm.latitude, lng: selectedStorm.longitude },
                    radius: radius,
                    ...drawingOptions.circleOptions
                  });
                  setCurrentShape(circle);
                  setSelectedStorm(null);
                  setStormImpactRadius(null);
                  // Trigger address search
                  getAddressesInBounds(circle.getBounds());
                  setDrawingMode(null);
                  setCurrentStep(3);
                }}
                style={{
                  marginTop: '10px',
                  width: '100%',
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                🎯 Find Leads in Damage Zone
              </button>

              {/* Damage zone info */}
              <div style={{
                marginTop: '8px',
                fontSize: '10px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                {selectedStorm.event_type === 'hail' && selectedStorm.magnitude >= 100
                  ? `~${Math.round((stormImpactRadius || getStormImpactRadius(selectedStorm)) / 1609 * 10) / 10} mile damage radius • likely roof claims`
                  : selectedStorm.event_type === 'tornado'
                  ? `Tornado path area • confirmed structural damage`
                  : `~${Math.round((stormImpactRadius || getStormImpactRadius(selectedStorm)) / 1609 * 10) / 10} mile wind damage zone`}
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Storm Impact Radius Circle - subtle damage zone indicator */}
        {selectedStorm && stormImpactRadius && (
          <Circle
            center={{ lat: selectedStorm.latitude, lng: selectedStorm.longitude }}
            radius={stormImpactRadius}
            options={{
              fillColor: selectedStorm.event_type === 'tornado' ? '#dc2626' : selectedStorm.event_type === 'hail' ? '#0ea5e9' : '#f97316',
              fillOpacity: 0.08,
              strokeColor: selectedStorm.event_type === 'tornado' ? '#dc2626' : selectedStorm.event_type === 'hail' ? '#0ea5e9' : '#f97316',
              strokeWeight: 2,
              strokeOpacity: 0.5,
              clickable: false,
              zIndex: 50
            }}
          />
        )}

        {/* Heatmap Layer - subtle background when storms filter is on */}
        {mapFilters.storms && heatmapData.length > 0 && (
          <HeatmapLayer
            data={heatmapData}
            options={{
              radius: currentZoom < 10 ? 35 : currentZoom < 12 ? 25 : currentZoom < 14 ? 18 : currentZoom < 16 ? 12 : 8,
              opacity: currentZoom < 12 ? 0.4 : currentZoom < 14 ? 0.35 : 0.25,
              dissipating: true,
              maxIntensity: 20,
              gradient: [
                'rgba(0, 255, 255, 0)',
                'rgba(0, 255, 255, 1)',
                'rgba(0, 191, 255, 1)',
                'rgba(0, 127, 255, 1)',
                'rgba(0, 63, 255, 1)',
                'rgba(0, 0, 255, 1)',
                'rgba(0, 0, 223, 1)',
                'rgba(0, 0, 191, 1)',
                'rgba(0, 0, 159, 1)',
                'rgba(0, 0, 127, 1)',
                'rgba(63, 0, 91, 1)',
                'rgba(127, 0, 63, 1)',
                'rgba(191, 0, 31, 1)',
                'rgba(255, 0, 0, 1)'
              ]
            }}
          />
        )}

        {/* Week Route: driving directions overlay */}
        {showWeekRoute && directionsResult && (
          <DirectionsRenderer
            directions={directionsResult}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: WEEK_PLAN[selectedRouteDay].color,
                strokeWeight: 5,
                strokeOpacity: 0.88,
                zIndex: 100
              }
            }}
          />
        )}

        {/* Week Route: numbered stop markers */}
        {showWeekRoute && directionsResult &&
          WEEK_PLAN[selectedRouteDay].stops.map((stop, idx) => {
            const legs = directionsResult.routes[0]?.legs || [];
            let position = null;
            if (idx < legs.length) {
              position = { lat: legs[idx].start_location.lat(), lng: legs[idx].start_location.lng() };
            } else if (idx === WEEK_PLAN[selectedRouteDay].stops.length - 1 && legs.length > 0) {
              position = { lat: legs[legs.length - 1].end_location.lat(), lng: legs[legs.length - 1].end_location.lng() };
            }
            if (!position) return null;
            return (
              <OverlayView
                key={`route-stop-${idx}`}
                position={position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <RouteStopMarker stop={stop} />
              </OverlayView>
            );
          })
        }

        {/* Selected lead marker */}
        {selectedLead && selectedLead.lat && selectedLead.lng && (
          <Marker
            position={{ lat: selectedLead.lat, lng: selectedLead.lng }}
            label={{
              text: '★',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
            icon={{
              path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
              fillColor: selectedLead.leadQuality === 'hot' ? '#ef4444' : '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 2.5,
              anchor: new window.google.maps.Point(12, 22),
              labelOrigin: new window.google.maps.Point(12, 10)
            }}
            title={selectedLead.streetAddress}
            animation={window.google.maps.Animation.DROP}
          />
        )}

        {/* Church markers */}
        {showChurches && churches.map((church, i) => (
          <Marker
            key={`church-${church.placeId || i}`}
            position={{ lat: church.lat, lng: church.lng }}
            icon={{
              url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" fill="%236366f1" stroke="white" stroke-width="2"/><text x="18" y="24" text-anchor="middle" fill="white" font-size="20">⛪</text></svg>'),
              scaledSize: new window.google.maps.Size(36, 36),
              anchor: new window.google.maps.Point(18, 18)
            }}
            onClick={() => setSelectedChurch(church)}
            zIndex={500}
          />
        ))}

        {/* Church info window */}
        {selectedChurch && (
          <InfoWindow
            position={{ lat: selectedChurch.lat, lng: selectedChurch.lng }}
            onCloseClick={() => setSelectedChurch(null)}
          >
            <div style={{ padding: '8px', minWidth: '180px', fontFamily: 'system-ui' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>⛪ {selectedChurch.name}</div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>{selectedChurch.address}</div>
              {selectedChurch.rating && <div style={{ fontSize: '12px', color: '#f59e0b' }}>{'⭐'.repeat(Math.round(selectedChurch.rating))} {selectedChurch.rating}</div>}
              <a
                href={`https://www.google.com/maps/place/?q=place_id:${selectedChurch.placeId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 600 }}
              >
                View on Google Maps →
              </a>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Stats badge removed - was blocking UI */}

      {/* Step Indicator */}
      {!showWeekRoute && (
        <div className="step-indicator">
          <div className={`step ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : ''}`}>
            <div className="step-number">{currentStep > 1 ? '✓' : '1'}</div>
            <span className="step-text">Find Area</span>
          </div>
          <div className={`step-divider ${currentStep > 1 ? 'completed' : ''}`}></div>
          <div className={`step ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : ''}`}>
            <div className="step-number">{currentStep > 2 ? '✓' : '2'}</div>
            <span className="step-text">Draw Selection</span>
          </div>
          <div className={`step-divider ${currentStep > 2 ? 'completed' : ''}`}></div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span className="step-text">Export Leads</span>
          </div>
        </div>
      )}

      {/* Week Route Stats Panel */}
      {showWeekRoute && (
        <div className="route-stats-panel">
          <div className="route-stats-header" style={{ borderLeftColor: WEEK_PLAN[selectedRouteDay].color }}>
            <span className="route-stats-day-label">{WEEK_PLAN[selectedRouteDay].label}</span>
            <a
              href="https://leadgenatr.pages.dev/FullWeekPlan"
              target="_blank"
              rel="noopener noreferrer"
              className="route-full-plan-link"
            >Full Plan →</a>
          </div>
          <div className="route-stats-grid">
            {[
              { label: 'Doors', value: WEEK_PLAN[selectedRouteDay].stats.doors },
              { label: 'Inspections', value: WEEK_PLAN[selectedRouteDay].stats.inspections },
              { label: 'Avg Job', value: WEEK_PLAN[selectedRouteDay].stats.avgJob },
              { label: 'Potential', value: WEEK_PLAN[selectedRouteDay].stats.potential, highlight: true },
              { label: 'Conversion', value: WEEK_PLAN[selectedRouteDay].stats.conversion },
            ].map(s => (
              <div key={s.label} className={`route-stat-item ${s.highlight ? 'highlight' : ''}`}>
                <div className="route-stat-val">{s.value}</div>
                <div className="route-stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="route-stops-list">
            {WEEK_PLAN[selectedRouteDay].stops.map((stop, idx) => (
              <a
                key={idx}
                className="route-stop-item"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className={`route-stop-num ${stop.quality}`}>{stop.num}</div>
                <div className="route-stop-info">
                  <div className="route-stop-addr">{stop.address.split(',')[0]}</div>
                  <div className="route-stop-owner">{stop.owner}</div>
                </div>
                {stop.roofAge && <div className="route-stop-tag">{stop.roofAge}yr roof</div>}
                {stop.value && !stop.roofAge && <div className="route-stop-tag">${Math.round(stop.value / 1000)}K</div>}
                <span className="route-stop-nav">📍</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default MapComponent;
